import { useCallback, useMemo } from "react";
import { useAuthStore } from "../stores/auth.store";
import { useOrganizationStore } from "../stores/organization.store";
import { OrganizationType } from "../types/organization.type";
import {
  canAccessOrg,
  getSitesForOrg,
  resolveZeroNavPermission,
  PERM_USER_WRITE,
  PERM_ROLE_ASSIGN,
  USER_MANAGEMENT_ROLES,
  canViewApprovalInbox,
} from "../auth";
import type { ZeroNavItem } from "../auth";
import { isReadOnlyAdmin } from "../utils/auth.helpers";

export function useAuthPermissions() {
  const {
    tokenPayload,
    permissionList,
    isPlatformAdmin: platformAdmin,
    user,
    canAccessPermission,
  } = useAuthStore();
  const { selectedOrganization } = useOrganizationStore();

  const orgId = selectedOrganization?.id ?? null;
  const readOnly = useMemo(
    () => isReadOnlyAdmin(user, tokenPayload),
    [user, tokenPayload],
  );

  const can = useCallback(
    (required: string, legacyRoles?: readonly string[]) =>
      canAccessPermission(required, legacyRoles, orgId),
    [canAccessPermission, orgId],
  );

  const canMutateAction = useCallback(
    (required: string, legacyRoles?: readonly string[]) =>
      !readOnly && can(required, legacyRoles),
    [readOnly, can],
  );

  const canSeeZeroNavItem = useCallback(
    (item: ZeroNavItem): boolean => {
      if (platformAdmin) {
        return true;
      }

      if (item.accessType === "orgMembership") {
        return canAccessOrg(tokenPayload, permissionList, orgId);
      }

      const perm = resolveZeroNavPermission(item, orgId);
      if (!perm) {
        return false;
      }
      if (item.entity === "approval" && canViewApprovalInbox(permissionList, orgId ?? undefined)) {
        return true;
      }
      return can(perm, item.legacyRoles);
    },
    [platformAdmin, tokenPayload, permissionList, orgId, can],
  );

  const filterZeroNavItems = useCallback(
    (items: ZeroNavItem[], orgType: OrganizationType): ZeroNavItem[] => {
      return items
        .filter((item) => item.allowedTypes.includes(orgType))
        .filter((item) => canSeeZeroNavItem(item));
    },
    [canSeeZeroNavItem],
  );

  const sitesForOrg = useCallback(
    (targetOrgId: string) => getSitesForOrg(tokenPayload, targetOrgId),
    [tokenPayload],
  );

  const canManageTeam = useCallback(
    (targetOrgId: string) =>
      canMutateAction(PERM_USER_WRITE(targetOrgId), USER_MANAGEMENT_ROLES),
    [canMutateAction],
  );

  const canAssignSites = useCallback(
    (targetOrgId: string) =>
      canMutateAction(PERM_ROLE_ASSIGN(targetOrgId), USER_MANAGEMENT_ROLES) ||
      (canMutateAction(PERM_USER_WRITE(targetOrgId), USER_MANAGEMENT_ROLES) &&
        can(`recyclee.${targetOrgId}.*`)),
    [canMutateAction, can],
  );

  const canViewApprovals = useCallback(
    () =>
      platformAdmin ||
      (orgId ? canViewApprovalInbox(permissionList, orgId) : canViewApprovalInbox(permissionList)),
    [platformAdmin, permissionList, orgId],
  );

  return {
    orgId,
    user,
    tokenPayload,
    permissionList,
    isPlatformAdmin: platformAdmin,
    isReadOnly: readOnly,
    can,
    canMutate: canMutateAction,
    canManageTeam,
    canAssignSites,
    canSeeZeroNavItem,
    filterZeroNavItems,
    sitesForOrg,
    canViewApprovals,
  };
}
